import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Stethoscope, Building2, Globe, Users, TrendingUp, Shield, 
  CheckCircle, Award, Clock, DollarSign, Headphones, BarChart3,
  Star, ArrowRight, Briefcase, Heart, Zap, Target
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import PublicHeader from '../components/PublicHeader';

export default function Partners() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const doctorBenefits = [
    {
      icon: <Globe className="w-6 h-6" />,
      title: 'Global Patient Reach',
      description: 'Connect with international patients from 50+ countries seeking quality healthcare in India'
    },
    {
      icon: <DollarSign className="w-6 h-6" />,
      title: 'Increased Revenue',
      description: 'Expand your practice with medical tourism patients and premium consultation fees'
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: 'Practice Analytics',
      description: 'Access detailed insights on patient inquiries, appointments, and revenue growth'
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Verified Profile',
      description: 'Build trust with verified credentials and authentic patient reviews'
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: 'Flexible Scheduling',
      description: 'Set your own availability for video consultations and in-person appointments'
    },
    {
      icon: <Headphones className="w-6 h-6" />,
      title: 'Dedicated Support',
      description: '24/7 support team to assist with patient coordination and administrative tasks'
    }
  ];

  const hospitalBenefits = [
    {
      icon: <Users className="w-6 h-6" />,
      title: 'International Patient Flow',
      description: 'Receive pre-qualified international patients seeking treatment at your facility'
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: 'Revenue Growth',
      description: 'Increase occupancy rates and revenue through medical tourism patients'
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: 'Brand Visibility',
      description: 'Showcase your accreditations, specialties, and success stories to global audience'
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: 'Targeted Marketing',
      description: 'Reach patients specifically looking for your specializations and treatments'
    },
    {
      icon: <Briefcase className="w-6 h-6" />,
      title: 'End-to-End Support',
      description: 'We handle patient coordination, visa assistance, and travel arrangements'
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: 'Performance Dashboard',
      description: 'Track inquiries, conversions, and patient satisfaction metrics in real-time'
    }
  ];

  const partnerStats = [
    { value: '2,500+', label: 'Partner Doctors', icon: <Stethoscope className="w-8 h-8" /> },
    { value: '450+', label: 'Partner Hospitals', icon: <Building2 className="w-8 h-8" /> },
    { value: '50+', label: 'Countries Served', icon: <Globe className="w-8 h-8" /> },
    { value: '98%', label: 'Partner Satisfaction', icon: <Star className="w-8 h-8" /> }
  ];

  const testimonials = [
    {
      name: 'Dr. Rajesh Kumar',
      role: 'Cardiologist, Apollo Hospital',
      avatar: '👨‍⚕️',
      content: 'Joining MedRahi has transformed my practice. I now treat patients from across the globe and have seen a 40% increase in consultations. The platform handles all the coordination seamlessly.',
      rating: 5
    },
    {
      name: 'Fortis Healthcare',
      role: 'Multi-Specialty Hospital Chain',
      avatar: '🏥',
      content: 'MedRahi has become our primary channel for international patients. The quality of patient referrals and the seamless coordination has made them an invaluable partner.',
      rating: 5
    },
    {
      name: 'Dr. Priya Sharma',
      role: 'Fertility Specialist, Nova IVF',
      avatar: '👩‍⚕️',
      content: 'The video consultation feature has been a game-changer. I can now provide initial consultations to patients abroad before they travel to India for treatment.',
      rating: 5
    }
  ];

  const howItWorks = {
    doctors: [
      { step: 1, title: 'Register & Verify', desc: 'Complete your profile with credentials and get verified' },
      { step: 2, title: 'Set Availability', desc: 'Configure your consultation hours and fees' },
      { step: 3, title: 'Receive Inquiries', desc: 'Get matched with patients seeking your expertise' },
      { step: 4, title: 'Consult & Earn', desc: 'Provide consultations and grow your practice' }
    ],
    hospitals: [
      { step: 1, title: 'Register Hospital', desc: 'Add your facility details and accreditations' },
      { step: 2, title: 'List Specialties', desc: 'Showcase your treatments and success rates' },
      { step: 3, title: 'Receive Patients', desc: 'Get pre-qualified patient referrals' },
      { step: 4, title: 'Provide Care', desc: 'Deliver world-class treatment to global patients' }
    ]
  };

  return (
    <div className="min-h-screen bg-white" data-testid="partners-page">
      <PublicHeader currentPage="partners" />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 relative overflow-hidden">
        <div className="absolute top-20 right-10 w-64 h-64 bg-blue-200/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-80 h-80 bg-purple-200/30 rounded-full blur-3xl"></div>
        
        <div className="mx-auto max-w-7xl relative">
          <div className="text-center">
            <Badge className="mb-6 bg-blue-100 text-blue-800 px-6 py-2 text-base">
              <Briefcase className="w-4 h-4 mr-2" />
              Partner With MedRahi
            </Badge>
            
            <h1 className="font-heading text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Join India's Leading
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                Medical Tourism Platform
              </span>
            </h1>
            
            <p className="text-xl text-gray-600 mb-10 max-w-3xl mx-auto leading-relaxed">
              Connect with international patients, grow your practice, and be part of India's healthcare revolution. 
              Join 2,500+ doctors and 450+ hospitals already transforming lives globally.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-8 py-6 text-lg"
                onClick={() => navigate('/register')}
                data-testid="register-doctor-hero-btn"
              >
                <Stethoscope className="w-5 h-5 mr-2" />
                Register as Doctor
              </Button>
              <Button
                size="lg"
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-6 text-lg"
                onClick={() => navigate('/register')}
                data-testid="register-hospital-hero-btn"
              >
                <Building2 className="w-5 h-5 mr-2" />
                Register as Hospital
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white border-b border-gray-100">
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {partnerStats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center text-white mx-auto mb-4">
                  {stat.icon}
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</div>
                <div className="text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Join - Doctors Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-cyan-50" data-testid="doctors-section">
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-blue-100 text-blue-800">
                <Stethoscope className="w-4 h-4 mr-2" />
                For Doctors
              </Badge>
              <h2 className="font-heading text-4xl font-bold text-gray-900 mb-6">
                Expand Your Practice Globally
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Join thousands of doctors who are already reaching international patients through MedRahi. 
                Our platform handles patient coordination, scheduling, and payments while you focus on delivering excellent care.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                {doctorBenefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-4 bg-white rounded-xl border border-blue-100">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 flex-shrink-0">
                      {benefit.icon}
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">{benefit.title}</h4>
                      <p className="text-sm text-gray-600">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <Button
                size="lg"
                className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white"
                onClick={() => navigate('/register')}
                data-testid="register-doctor-section-btn"
              >
                Register as Doctor
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-2xl border-2 border-blue-100">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Zap className="w-5 h-5 text-blue-600" />
                How It Works for Doctors
              </h3>
              <div className="space-y-6">
                {howItWorks.doctors.map((step, idx) => (
                  <div key={idx} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                      {step.step}
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{step.title}</h4>
                      <p className="text-sm text-gray-600">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Join - Hospitals Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-50 to-pink-50" data-testid="hospitals-section">
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1 bg-white rounded-3xl p-8 shadow-2xl border-2 border-purple-100">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Zap className="w-5 h-5 text-purple-600" />
                How It Works for Hospitals
              </h3>
              <div className="space-y-6">
                {howItWorks.hospitals.map((step, idx) => (
                  <div key={idx} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                      {step.step}
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{step.title}</h4>
                      <p className="text-sm text-gray-600">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <Badge className="mb-4 bg-purple-100 text-purple-800">
                <Building2 className="w-4 h-4 mr-2" />
                For Hospitals
              </Badge>
              <h2 className="font-heading text-4xl font-bold text-gray-900 mb-6">
                Attract International Patients
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Partner with MedRahi to showcase your hospital to medical tourists worldwide. 
                We bring you pre-qualified patients and handle all coordination from visa assistance to post-treatment follow-ups.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                {hospitalBenefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-4 bg-white rounded-xl border border-purple-100">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center text-purple-600 flex-shrink-0">
                      {benefit.icon}
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">{benefit.title}</h4>
                      <p className="text-sm text-gray-600">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <Button
                size="lg"
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white"
                onClick={() => navigate('/register')}
                data-testid="register-hospital-section-btn"
              >
                Register as Hospital
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-testid="testimonials-section">
        <div className="mx-auto max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-4xl font-bold text-gray-900 mb-4">
              Trusted by Healthcare Leaders
            </h2>
            <p className="text-xl text-gray-600">
              Hear from doctors and hospitals who have grown with MedRahi
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, idx) => (
              <Card key={idx} className="border-2 border-gray-100 hover:border-blue-200 transition-colors">
                <CardContent className="p-6">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-6 leading-relaxed italic">
                    "{testimonial.content}"
                  </p>
                  <div className="flex items-center gap-4 border-t border-gray-100 pt-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center text-2xl">
                      {testimonial.avatar}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{testimonial.name}</p>
                      <p className="text-sm text-gray-600">{testimonial.role}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="mx-auto max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-4xl font-bold text-gray-900 mb-4">
              Partner Requirements
            </h2>
            <p className="text-xl text-gray-600">
              Simple criteria to ensure quality healthcare for our patients
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Doctor Requirements */}
            <Card className="border-2 border-blue-200">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Stethoscope className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">For Doctors</h3>
                </div>
                <ul className="space-y-4">
                  {[
                    'Valid medical license from recognized council',
                    'Minimum 5 years of clinical experience',
                    'Specialization degree (MD/MS/DM/MCh)',
                    'Hospital or clinic affiliation',
                    'Malpractice insurance coverage',
                    'English communication proficiency'
                  ].map((req, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                      <span className="text-gray-700">{req}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Hospital Requirements */}
            <Card className="border-2 border-purple-200">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-purple-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">For Hospitals</h3>
                </div>
                <ul className="space-y-4">
                  {[
                    'NABH or JCI accreditation (preferred)',
                    'Minimum 50 beds capacity',
                    'International patient department',
                    'English-speaking staff',
                    'Modern diagnostic facilities',
                    'Medical tourism visa letter capability'
                  ].map((req, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                      <span className="text-gray-700">{req}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white">
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="font-heading text-4xl lg:text-5xl font-bold mb-6">
            Ready to Grow Your Practice?
          </h2>
          <p className="text-xl mb-10 opacity-95">
            Join MedRahi today and connect with patients from around the world. 
            Registration is free and takes less than 5 minutes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-white text-blue-600 hover:bg-gray-100 font-semibold text-lg px-8 py-6"
              onClick={() => navigate('/register')}
              data-testid="cta-register-doctor-btn"
            >
              <Stethoscope className="w-5 h-5 mr-2" />
              Register as Doctor
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white/10 font-semibold text-lg px-8 py-6"
              onClick={() => navigate('/register')}
              data-testid="cta-register-hospital-btn"
            >
              <Building2 className="w-5 h-5 mr-2" />
              Register as Hospital
            </Button>
          </div>
          <p className="mt-8 text-sm opacity-75">
            Have questions? Contact our partner support team at partners@medrahi.com
          </p>
        </div>
      </section>
    </div>
  );
}
